import { useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { FancyBrand } from "@/components/fancy-brand"
import { Button } from "@/components/ui/button"
import { MASCOT_INTRINSIC } from "@/lib/brand-assets"
import { businessLocationLabel } from "@/lib/business-info"
import { useGoogleReviews } from "@/hooks/use-google-reviews"

function SmokeEffect() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const resize = () => {
      canvas.width = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
    }
    resize()
    window.addEventListener("resize", resize)

    interface Particle {
      x: number
      y: number
      radius: number
      opacity: number
      speed: number
      drift: number
    }

    const particles: Particle[] = []

    const createParticle = (): Particle => ({
      x: Math.random() * canvas.width,
      y: canvas.height + 20,
      radius: Math.random() * 40 + 15,
      opacity: Math.random() * 0.08 + 0.02,
      speed: Math.random() * 0.5 + 0.2,
      drift: (Math.random() - 0.5) * 0.3,
    })

    for (let i = 0; i < 20; i++) {
      const p = createParticle()
      p.y = Math.random() * canvas.height
      particles.push(p)
    }

    let animId: number

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]
        p.y -= p.speed
        p.x += p.drift
        p.opacity *= 0.998
        p.radius += 0.05

        if (p.y < -50 || p.opacity < 0.005) {
          particles[i] = createParticle()
          continue
        }

        const gradient = ctx.createRadialGradient(
          p.x,
          p.y,
          0,
          p.x,
          p.y,
          p.radius
        )
        gradient.addColorStop(0, `rgba(34, 197, 94, ${p.opacity})`)
        gradient.addColorStop(1, "rgba(34, 197, 94, 0)")

        ctx.beginPath()
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2)
        ctx.fillStyle = gradient
        ctx.fill()
      }

      animId = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      cancelAnimationFrame(animId)
      window.removeEventListener("resize", resize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  )
}

export function Hero() {
  const reviews = useGoogleReviews()

  return (
    <section
      id="home"
      className="relative flex min-h-screen items-center overflow-hidden pt-20"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-zielarz-green/5 via-transparent to-transparent" />

      {/* Smoke effect */}
      <SmokeEffect />

      {/* Decorative cannabis leaves - large blurred background */}
      <div className="absolute -top-20 -right-20 size-96 rounded-full bg-zielarz-green/5 blur-3xl" />
      <div className="absolute -bottom-20 -left-20 size-72 rounded-full bg-zielarz-lime/5 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-2 lg:gap-8">
        {/* Left content */}
        <div className="flex flex-col justify-center">
          <p className="mb-3 text-sm font-medium tracking-wide text-muted-foreground uppercase">
            {businessLocationLabel}
          </p>

          <h1 className="mb-6 text-4xl leading-tight font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            <FancyBrand size="xl" className="align-baseline italic" />
            <span className="text-foreground"> - Twój Lokalny</span>
            <br />
            <span className="text-foreground">Vape Shop w Częstochowie.</span>
          </h1>

          <p className="mb-8 max-w-lg text-lg text-muted-foreground">
            Najlepszy wybór liquidów, soli nikotynowych i longfilli w
            Częstochowie. Profesjonalna obsługa, świetne ceny i przyjazna
            atmosfera. Wpadnij do nas!
          </p>

          <div className="flex flex-wrap gap-4">
            <Button
              asChild
              className="h-12 rounded-full bg-zielarz-green px-8 text-base font-bold text-white transition-all hover:bg-zielarz-green-light hover:shadow-lg hover:shadow-zielarz-green/25"
            >
              <Link to="/smaki">Sprawdź Smaki</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="h-12 rounded-full border-zielarz-green/30 px-8 text-base font-bold text-zielarz-green hover:bg-zielarz-green/10"
            >
              <a href="#o-nas">O nas</a>
            </Button>
          </div>

          {/* Quick stats */}
          <div className="mt-12 flex gap-8 border-t border-border/50 pt-8">
            <div>
              <div className="text-2xl font-extrabold text-zielarz-green">
                100+
              </div>
              <div className="text-sm text-muted-foreground">Smakow</div>
            </div>
            <div>
              <div className="text-2xl font-extrabold text-zielarz-green">
                3+
              </div>
              <div className="text-sm text-muted-foreground">
                Lata doświadczenia
              </div>
            </div>
            {reviews ? (
              <div>
                <div className="text-2xl font-extrabold text-zielarz-green">
                  {reviews.rating.toFixed(1)}
                </div>
                <div className="text-sm text-muted-foreground">
                  Ocena Google
                  {reviews.userRatingCount > 0
                    ? ` (${reviews.userRatingCount} opinii)`
                    : ""}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Right side - mascot only */}
        <div className="hidden items-center justify-center lg:flex">
          <div className="animate-float relative">
            {/* Glow ring */}
            <div className="animate-pulse-glow absolute -inset-8 rounded-full bg-zielarz-green/5" />

            <div className="relative flex size-80 items-center justify-center overflow-hidden rounded-[2.5rem] border-2 border-zielarz-green/20 bg-gradient-to-br from-zielarz-green/10 to-zielarz-lime/10 shadow-2xl shadow-zielarz-green/10 xl:size-96">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.22),transparent_42%)]" />
              <div className="absolute inset-8 rounded-[2rem] border border-white/12 bg-[linear-gradient(135deg,rgba(10,10,10,0.84),rgba(90,90,90,0.58))] shadow-[0_18px_40px_rgba(0,0,0,0.24)] backdrop-blur-xl" />
              <div className="relative z-10 flex h-full w-full items-center justify-center p-16">
                <img
                  src="/assets/brand/mascot.svg"
                  alt="Logo Zielarz"
                  width={MASCOT_INTRINSIC.width}
                  height={MASCOT_INTRINSIC.height}
                  className="h-full w-full object-contain drop-shadow-[0_18px_40px_rgba(0,0,0,0.32)]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
